import type { ReactNode } from 'react';

import { Finance, General } from '../StrokeIcons';
import type { ColorName, Style } from './buttonClasses';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import FilterSelect from './FilterSelect';
import type { Size, UpdateStatus } from './types';

export type TagOption = {
  value: string;
  label: string;
};

export type TagFilterSelectProps = {
  tagOptions: TagOption[];
  value?: string;
  /** The default/reset value. When provided and value !== defaultValue, shows X mark to clear. */
  defaultValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  includeRemoveOption?: boolean;
  size?: Size;
  disabled?: boolean;
  /** Show the selected label/placeholder text. Default true. */
  showText?: boolean;
  /** Show the dropdown chevron. Default true. */
  showChevron?: boolean;
  bordered?: boolean;
  shape?: 'rounded' | 'pill' | 'square';
  color?: ColorName;
  tone?: Style;
  tooltip?: ReactNode;
  /** Status for async operations: 'updating' shows spinner, 'success' shows checkmark, 'error' shows warning. */
  status?: UpdateStatus;
  /** Called when user wants to create a new tag from the filter query. */
  onCreate?: (value: string) => void;
  /** Custom clear handler. When provided with defaultValue, called instead of onChange(defaultValue). */
  onClear?: () => void;
};

export default function TagFilterSelect({
  tagOptions,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select tag',
  className,
  includeRemoveOption,
  size,
  disabled,
  showText,
  showChevron,
  bordered,
  shape,
  color,
  tone,
  tooltip,
  status,
  onCreate,
  onClear,
}: TagFilterSelectProps) {
  let options = tagOptions.map((tag) => ({
    value: tag.value,
    label: tag.label,
    icon: <Finance.Tag01 />,
  }));

  if (includeRemoveOption) {
    options = [{ value: '', label: 'Remove tag', icon: <General.Trash01 /> }, ...options];
  }

  const showClearButton = defaultValue !== undefined && value !== defaultValue;

  const select = (
    <FilterSelect
      icon={<Finance.Tag01 />}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      status={status}
      disabled={disabled}
      autoWidth
      size={size}
      showText={showText}
      showChevron={showClearButton ? false : showChevron}
      bordered={bordered}
      shape={shape}
      color={color}
      tone={tone}
      tooltip={tooltip}
      onCreate={onCreate}
    />
  );

  if (showClearButton) {
    return (
      <ButtonGroup size={size}>
        {select}
        <Button icon={<General.XMark />} onClick={onClear ?? (() => onChange(defaultValue))} />
      </ButtonGroup>
    );
  }

  return select;
}
